import { useEffect, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, Users, Crown, Video, Clock, TrendingUp, Download, Search } from "lucide-react"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts"
import { Button } from "@/clientWeb/components/ui/button"
import { Badge } from "@/clientWeb/components/ui/badge"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { SkeletonList } from "@/clientWeb/components/ui/page-loader"
import { NoData } from "@/clientWeb/components/ui/no-data"
import nodataAnalyticsImg from "@/assets/nodataanalytics.svg"
import { analyticsService, type AnalyticsLeaderboardEntry } from "./services/analytics.service"

const COLORS = ["#6366f1", "#8b5cf6", "#a78bfa", "#c4b5fd", "#ddd6fe", "#e0e7ff", "#eef2ff", "#f3f4f6", "#f9fafb", "#fff"]

function Avatar({ user }: { user: AnalyticsLeaderboardEntry }) {
  const initials = user.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const ci = user.name.charCodeAt(0) % colors.length
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden ${colors[ci]}`}>
      {user.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" /> : initials}
    </div>
  )
}

export default function TopUsersPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [sortBy, setSortBy] = useState<"meetings" | "hours" | "recordings">("meetings")
  const [users, setUsers] = useState<AnalyticsLeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    analyticsService.getLeaderboard()
      .then((data) => { if (!cancelled) setUsers(data) })
      .catch((err) => {
        if (cancelled) return
        toast.error({ title: 'Failed to load leaderboard', description: err instanceof Error ? err.message : 'Please try again' })
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const filtered = users
    .filter(u => u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => b[sortBy] - a[sortBy])

  const chartData = users.slice(0, 7).map(u => ({
    name: u.name.split(" ")[0],
    meetings: u.meetings,
    hours: u.hours,
  }))

  if (loading) {
    return (
      <div className="flex flex-col flex-1 h-full overflow-y-auto p-4 sm:p-6 gap-5">
        <SkeletonList rows={6} />
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 h-full overflow-y-auto p-4 sm:p-6 gap-5 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 dark:from-slate-900 dark:via-slate-800/50 dark:to-slate-900">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="rounded-full h-9 w-9 shrink-0" onClick={() => navigate("/analytics")}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("analytics.topUsers")}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("analytics.topUsersDesc")}</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Download className="w-4 h-4" /> {t("common.download")}
        </Button>
      </div>

      {/* Chart */}
      <div className="bg-card border border-border rounded-2xl p-5 shadow-xs">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">{t("analytics.top7ByMeetingCount")}</h3>
        </div>
        <div className="h-52">
          {chartData.length === 0 ? (
            <NoData image={nodataAnalyticsImg} title={t("analytics.noDataAvailable")} size="sm" showActionIcon={false} />
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} width={30} />
              <Tooltip
                contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12 }}
                cursor={{ fill: "var(--muted)" }}
              />
              <Bar dataKey="meetings" radius={[6, 6, 0, 0]} name={t("common.meetings")}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-2xl shadow-xs overflow-hidden">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2 flex-1">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t("analytics.searchUsersPlaceholder")}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {(["meetings", "hours", "recordings"] as const).map(key => (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                  sortBy === key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
                }`}
              >
                {key}
              </button>
            ))}
          </div>
        </div>

        <div className="divide-y divide-border/50">
          {filtered.map((user, idx) => (
            <div key={user.rank} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
              <div className="w-7 text-center shrink-0">
                {idx === 0 ? (
                  <Crown className="w-4 h-4 text-amber-400 mx-auto" />
                ) : (
                  <span className="text-xs font-mono text-muted-foreground">{idx + 1}</span>
                )}
              </div>
              <Avatar user={user} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
              <div className="hidden sm:flex items-center gap-4 shrink-0">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Video className="w-3.5 h-3.5" />{user.meetings} {t("common.meetings")}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" />{user.hours}h
                </div>
              </div>
              <Badge variant="secondary" className="text-xs shrink-0">
                {user[sortBy]} {sortBy === "hours" ? t("analytics.hrs") : t("analytics." + sortBy)}
              </Badge>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <Users className="w-10 h-10 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">{t("analytics.noUsersFound")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
